import * as preact from "preact";

import * as accountModal from "accountModal";
import UserWordModel from "models/UserWordModel";
import models from "models/models";
import readlangClient from "readlangClient";
import MiniMarkdownView from "views/MiniMarkdownView";

interface Props {
  userWord: UserWordModel;
  context: string;
}

interface State {
  askAIResponse?: string;
  askAIRequest?: string;
  askAIFetching?: boolean;

  recentPrompts?: {
    prompt: string;
    _id: string;
    pinned: boolean;
  }[];
  showRecentPrompts?: boolean;
}

class AskAIView extends preact.Component<Props, State> {
  inputContainer: HTMLDivElement;

  constructor(props: Props) {
    super(props);

    this.state = {};

    this.fetchRecentPrompts();
    window.document.addEventListener("click", this.onDocumentClick);
  }

  componentWillUnmount(): void {
    window.document.removeEventListener("click", this.onDocumentClick);
  }

  onDocumentClick = (event) => {
    if (
      this.state.showRecentPrompts &&
      this.inputContainer &&
      $.contains(this.inputContainer, event.target)
    ) {
      // Don't do anything in this case, since the user is interacting with the recent prompts
      return;
    }

    this.setState({ showRecentPrompts: false });
  };

  async fetchRecentPrompts() {
    const recentPrompts = (
      await readlangClient.request(
        "GET",
        "/api/smartDictionary/recentAskAIPrompts",
        {
          language: this.props.userWord.get("language"),
        }
      )
    ).data.prompts;

    this.setState({ recentPrompts });
  }

  render() {
    return (
      <div class="askAIView">
        <div class="inputRow">
          <div
            class="inputContainer"
            onMouseEnter={() => this.setState({ showRecentPrompts: true })}
            onMouseLeave={() => this.setState({ showRecentPrompts: false })}
            ref={(element) => (this.inputContainer = element)}
          >
            <input
              type="text"
              placeholder={`Ask AI about this ${this.props.userWord.isPhrase() ? "phrase" : "word"}. e.g. "Explain the grammar"`}
              onInput={(event) => {
                this.setState({
                  askAIRequest: event.currentTarget.value,
                  showRecentPrompts: false,
                });
              }}
              value={this.state.askAIRequest}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  this.askAI();
                }
              }}
              onFocus={() => {
                this.setState({ showRecentPrompts: true });
              }}
            />
            {this.state.recentPrompts?.length > 0 &&
            this.state.showRecentPrompts ? (
              <div class="recentPrompts">
                <h3>Reuse recent prompt</h3>
                <ul>
                  {this.state.recentPrompts.map((prompt) => (
                    <li>
                      <div
                        class={`pinButton${prompt.pinned ? " pinned" : ""}`}
                        onClick={async () => {
                          const newPrompts = (
                            await readlangClient.request(
                              "POST",
                              `/api/smartDictionary/askAIPrompt/${prompt._id}/pin`,
                              {
                                value: !prompt.pinned,
                              }
                            )
                          ).data.prompts;

                          this.setState({
                            recentPrompts: newPrompts,
                          });
                        }}
                      >
                        <i class="icon-pushpin" />
                      </div>
                      <div
                        class="prompt"
                        onClick={async () => {
                          await this.setState({
                            askAIRequest: prompt.prompt,
                            showRecentPrompts: false,
                          });
                          this.askAI();
                        }}
                      >
                        {prompt.prompt}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
          <button onClick={() => this.askAI()}>Ask&nbsp;AI</button>
        </div>
        <p class="aiResponse">
          {this.state.askAIFetching ? (
            "Fetching..."
          ) : this.state.askAIResponse ? (
            <MiniMarkdownView text={this.state.askAIResponse} />
          ) : null}
        </p>
      </div>
    );
  }

  renderAIResponse() {
    return;
  }

  upgrade() {
    accountModal.show({
      model: models.user,
      title: "askAI",
      messageHtml: `<div>
        <h2>Upgrade to Ask AI</h2>
        <p>
          Ask questions related to new vocabulary: grammar, synonyms, culture, or
          whatever you are curious about!
        </p>
      </div>`,
      showTitle: false,
      smartDefinitions: true,
    });
  }

  async askAI() {
    if (!models.user.hasPremiumAccess()) {
      this.upgrade();
      return;
    }

    this.setState({ askAIFetching: true, showRecentPrompts: false });
    readlangClient.request(
      "GET",
      "/api/smartDictionary/askAI",
      {
        language: this.props.userWord.get("language"),
        word: this.props.userWord.get("word"),
        context: this.props.context,
        prompt: this.state.askAIRequest,
      },
      (explanation) => {
        // If the explanation starts and ends with quotes, remove them
        // (not doing on server since it's streaming the response, so we don't know what the last
        // character is until the end of the response)
        if (
          explanation[0] === '"' &&
          explanation[explanation.length - 1] === '"'
        ) {
          explanation = explanation.slice(1, -1);
        }

        this.setState({
          askAIResponse: explanation,
          askAIFetching: false,
          showRecentPrompts: false,
        });
      },
      undefined,
      { dataType: "text" }
    );
  }
}

export default AskAIView;
